import { z } from 'zod';

import { companySchema } from './company';
import { idSchema } from './utils';

export type ProjectPathParams = z.infer<typeof projectPathParamsSchema>;
export const projectPathParamsSchema = z.object({
  projectId: idSchema('projectId'),
});

export type ProjectStage = z.infer<typeof projectStageSchema>;
export const projectStageSchema = z.union([
  z.literal('planning'),
  z.literal('installation'),
  z.literal('documentation'),
  z.literal('operative'),
  z.literal('archived'),
]);
export const projectGatewayStatusSchema = z.object({
  gatewayCountOk: z.number(),
  gatewayCountTotal: z.number(),
});

export type ProjectSensorStatus = z.infer<typeof projectSensorStatusSchema>;
export const projectSensorStatusSchema = z.object({
  sensorCountOk: z.number(),
  sensorCountWarning: z.number(),
  sensorCountError: z.number(),
  sensorCountInactive: z.number(),
});
export const projectSearchUserSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  companyName: z.string(),
});

export type WoodMoistureSettings = z.infer<typeof woodMoistureSettingsSchema>;
export const woodMoistureSettingsSchema = z.object({
  redAlert: z.number().min(0).max(50),
  yellowAlert: z.number().min(0).max(50),
  shouldAlert: z.boolean(),
});

export type Project = z.infer<typeof projectSchema>;
export const projectSchema = z.object({
  projectId: z.number(),
  companyId: z.number(),
  createdAt: z.string(),
  InstallationDate: z.string().nullable().optional(),
  operativeDate: z.string().nullable().optional(),
  archivedDate: z.string().nullable().optional(),
  hiddenDate: z.string().nullable().optional(),
  name: z.string().max(150),
  timezone: z.string(),
  address: z.string(),
  company: companySchema.nullable().optional(),
  projectStage: projectStageSchema,
  gatewayStatus: projectGatewayStatusSchema.nullable(),
  sensorStatus: projectSensorStatusSchema.nullable(),
  sensorStatusReference: projectSensorStatusSchema.nullable(),
  latitude: z.string().nullable().optional(),
  longitude: z.string().nullable().optional(),
  crmReference: z.string().max(50),
  isSalesDemo: z.boolean().optional(),
  isDemoProject: z.boolean(),
  isUserHidden: z.boolean(),
  buildingOwner: z.string().max(255).nullable(),
  buildingOwnerContactEmail: z.string().email().nullable(),
  vatNr: z.string().max(20).nullable(),
  team: z.array(projectSearchUserSchema).nullable(),
  woodMoistureSettings: woodMoistureSettingsSchema.default({
    redAlert: 20,
    yellowAlert: 18,
    shouldAlert: true,
  }),
});

export type CreateProjectBody = z.infer<typeof createProjectBodySchema>;
export const createProjectBodySchema = z.object({
  projectName: z.string().min(1).max(150),
  companyId: z.number().optional(),
  companyName: z.string().max(150),
  isDemoProject: z.boolean(),
  streetAddress: z.string().min(1).max(120),
  latitude: z.number(),
  longitude: z.number(),
  crmReference: z.string().max(50).optional(),
  buildingOwner: z.string().max(255).optional(),
  buildingOwnerContactEmail: z.string().email().optional(),
  vatNr: z.string().max(20).optional(),
});

export type UpdateProjectBody = z.infer<typeof updateProjectBodySchema>;
export const updateProjectBodySchema = z.object({
  projectName: z.string().min(1).max(150).optional(),
  companyId: z.number().optional(),
  companyName: z.string().max(150).optional(),
  isDemoProject: z.boolean(),
  isUserHidden: z.boolean().optional(),
  streetAddress: z.string().min(1).max(120).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  crmReference: z.string().max(50).optional(),
  projectStage: projectStageSchema.optional(),
  installationDate: z.string().datetime().optional(),
  operativeDate: z.string().datetime().optional(),
  archivedDate: z.string().datetime().optional(),
  hiddenDate: z.string().datetime().optional(),
  buildingOwner: z.string().max(255).optional(),
  buildingOwnerContactEmail: z.string().email().optional(),
  vatNr: z.string().max(20).optional(),
  woodMoistureSettings: woodMoistureSettingsSchema.optional(),
});
